import React from 'react';
import { toast } from 'sonner';
import { Button, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

/**
 * @typedef {import('@mui/material').ButtonProps & {
 *   textToCopy: string | (() => string);
 *   iconOnLeft?: boolean;
 *   noHighlight?: boolean;
 *   alwaysShowIcon?: boolean;
 *   children?: React.ReactNode;
 *   sx?: Object;
 *   iconProps?: React.ComponentProps<typeof import('@mui/material').SvgIcon>;
 * }} CopyButtonProps
 */
/**
 * Copy button component
 * @param {CopyButtonProps} props - The component props
 * @returns {JSX.Element} The copy button component
 */
export const CopyButton = ({
  textToCopy,
  iconOnLeft = false,
  noHighlight = false,
  alwaysShowIcon = false,
  children,
  sx,
  iconProps,
  onMouseEnter,
  onMouseLeave,
  ...props
}) => {
  const [isCopied, setIsCopied] = React.useState(false);
  const [isHovering, setIsHovering] = React.useState(false);
  const theme = useTheme();

  const handleCopy = async () => {
    try {
      const text = typeof textToCopy === 'function' ? textToCopy() : textToCopy;

      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1000);
    } catch (err) {
      toast.error('Failed to copy text');
    }
  };

  const handleMouseEnter = (/** @type {React.MouseEvent<HTMLButtonElement>} */ e) => {
    onMouseEnter?.(e);
    setIsHovering(true);
  };

  const handleMouseLeave = (/** @type {React.MouseEvent<HTMLButtonElement>} */ e) => {
    onMouseLeave?.(e);
    setIsHovering(false);
  };

  return (
    <Button
      variant="text"
      onClick={handleCopy}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      disableRipple={noHighlight}
      sx={{
        width: 'fit-content',
        minWidth: 'fit-content',
        textTransform: 'none',
        display: 'flex',
        alignItems: 'center',
        flexDirection: iconOnLeft ? 'row-reverse' : 'row',
        gap: 1,
        ...(noHighlight && {
          backgroundColor: 'transparent',
          padding: 0,
          height: '1rem',
        }),
        ...theme.typography.body2,
        ...sx,
      }}
      {...props}
    >
      {children}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <CheckIcon sx={{ fontSize: 'inherit', opacity: 0 }} />
        {isCopied ? (
          <CheckIcon {...iconProps} sx={{ fontSize: 'inherit', position: 'absolute', inset: 0, ...iconProps?.sx }} />
        ) : (
          (isHovering || alwaysShowIcon) && (
            <ContentCopyIcon
              {...iconProps}
              sx={{ fontSize: 'inherit', position: 'absolute', inset: 0, ...iconProps?.sx }}
            />
          )
        )}
      </div>
    </Button>
  );
};
